import z from "zod";

/**
 * Schema para los nombres de modalidades en diferentes idiomas
 */
export const modalityNameSchema = z.object({
  en: z.string(),
  es: z.string(),
});

/**
 * Schema para una modalidad completa
 */
export const modalitySchema = z.object({
  abbr: z.string(),
  name: modalityNameSchema,
});

/**
 * Schema para la respuesta de la API de modalidades
 */
export const modalitiesResponseSchema = z.array(modalitySchema);

// Tipos TypeScript derivados de los schemas
export type ModalityName = z.infer<typeof modalityNameSchema>;
export type Modality = z.infer<typeof modalitySchema>;
export type ModalitiesResponse = z.infer<typeof modalitiesResponseSchema>;

/**
 * Utilidad para obtener el nombre de una modalidad en un idioma específico
 * @param modality - La modalidad
 * @param language - El idioma deseado ('en' | 'es')
 * @returns El nombre en el idioma especificado, o la abreviación si no existe
 */
export function getModalityName(
  modality: Modality,
  language: "en" | "es" = "es"
): string {
  return modality.name[language] || modality.abbr;
}

/**
 * Utilidad para buscar una modalidad por su abreviación
 * @param modalities - Array de modalidades
 * @param abbr - La abreviación a buscar
 * @returns La modalidad encontrada o undefined
 */
export function findModalityByAbbr(
  modalities: Modality[],
  abbr: string
): Modality | undefined {
  return modalities.find((modality) => modality.abbr === abbr);
}

/**
 * Utilidad para obtener el nombre completo de una modalidad por su abreviación
 * @param modalities - Array de modalidades
 * @param abbr - La abreviación a buscar
 * @param language - El idioma deseado ('en' | 'es')
 * @returns El nombre completo o la abreviación si no se encuentra
 */
export function getModalityNameByAbbr(
  modalities: Modality[],
  abbr: string,
  language: "en" | "es" = "es"
): string {
  const modality = findModalityByAbbr(modalities, abbr);
  return modality ? getModalityName(modality, language) : abbr;
}
